import axios, {
  AxiosError,
  AxiosInstance,
  AxiosResponse
} from 'axios';
import { useUIStore } from '@/stores/useUIStore';
import { queryClient } from '@/lib/queryClient';
import { isManageSubdomain } from './authContext';

/**
 * Cliente HTTP basado en Axios con configuración global
 * Maneja automáticamente la autenticación, errores y configuración base
 */
const axiosClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Importante para manejar cookies de autenticación
  timeout: 30000, // 30 segundos de timeout
  xsrfCookieName: 'XSRF-TOKEN',
  xsrfHeaderName: 'X-CSRF-Token',
});

// Interceptor para añadir el contexto de autenticación y otros headers necesarios
axiosClient.interceptors.request.use(
  (config) => {
    // ✅ Añadir contexto de autenticación (Admin vs User)
    if (isManageSubdomain()) {
      config.headers['x-auth-context'] = 'admin';
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Variables para control de refresh
let isRefreshing = false;
let lastRefreshAttempt = 0;
const REFRESH_COOLDOWN = 5000; // 5 segundos de espera entre intentos de refresh

let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Interceptor para manejo global de errores con refresh automático
axiosClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Para respuestas exitosas, devolver solo los datos
    return response.data as any;
  },
  async (error: AxiosError) => {
    const originalRequest: any = error.config;

    if (error.response) {
      const { status, data } = error.response;
      const errorMessage = (data as any)?.message || error.message || 'Error de conexión';
      error.message = errorMessage;

      // Manejo de 401 con refresh automático
      if (status === 401 && !originalRequest._retry) {
        const currentPath = window.location.pathname;
        const isAuthPage = currentPath.includes('/login') || currentPath.includes('/register') || currentPath.includes('/user-auth');

        if (isAuthPage) {
          return Promise.reject(error);
        }

        const isGetRequest = originalRequest.method?.toUpperCase() === 'GET';
        const isStorefront = originalRequest.url?.includes('/storefront/');
        const isSafeToFailRoute = [
          '/admin/configuration',
          '/auth/refresh',
          // NOTE: /auth/me must NOT be here — it needs to trigger refresh when access_token expires
        ].some(safePath => originalRequest.url?.includes(safePath));

        const isSilent401 = (isGetRequest && isStorefront) || isSafeToFailRoute;

        if (originalRequest.url?.includes('/auth/login') ||
          originalRequest.url?.includes('/auth/register') ||
          originalRequest.url?.includes('/auth/refresh')) {
          return Promise.reject(error);
        }

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(() => {
            return axiosClient(originalRequest);
          });
        }

        const now = Date.now();
        if (now - lastRefreshAttempt < REFRESH_COOLDOWN) {
          console.log('Refresh en cooldown, rechazando petición silenciosamente');
          return Promise.reject(error);
        }

        originalRequest._retry = true;
        isRefreshing = true;
        lastRefreshAttempt = now;

        try {
          // Detectar contexto antes del fetch
          const isAdminPanel = isManageSubdomain();

          // Se ha quitado el check is_logged_in ya que confiamos en la validez inherente del token
          if (process.env.NODE_ENV === 'development') {
            console.debug(`[REFRESH] Iniciando refresh: ${isAdminPanel ? 'admin' : 'user'}`);
          }

          const getCsrfToken = () => {
            const match = document.cookie.match(/XSRF-TOKEN=([^;]+)/);
            return match ? decodeURIComponent(match[1]) : '';
          };

          const refreshResponse = await fetch('/api/auth/refresh', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'x-auth-context': isAdminPanel ? 'admin' : 'user',
              'X-CSRF-Token': getCsrfToken()
            },
            credentials: 'include'
          });

          if (!refreshResponse.ok) {
            const errorData = await refreshResponse.json().catch(() => ({}));
            if (process.env.NODE_ENV === 'development') {
              console.debug(' [REFRESH] Falló (probablemente sin sesión):', errorData.message || refreshResponse.statusText);
            }
            throw new Error(errorData.message || 'Refresh token invalid');
          }

          if (process.env.NODE_ENV === 'development') {
            console.log(' [REFRESH] Éxito. Reintentando petición original...');
          }

          processQueue(null, 'refreshed');
          isRefreshing = false;
          return axiosClient(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          isRefreshing = false;

          //  Limpiar la sesión en React Query solo si el refresh falló definitivamente
          queryClient.setQueryData(['session'], null);

          if (!isSilent401) {
            const isAdminPanel = isManageSubdomain();

            if (isAdminPanel) {
              console.warn(' Admin session expired, redirecting to login...');
              if (!window.location.pathname.includes('/login')) {
                window.location.href = '/login?admin=true';
              }
            } else {
              console.warn(' Session expired on a critical action, opening Auth Modal...');
              useUIStore.getState().openAuthModal('login');
            }
          }
          return Promise.reject(refreshError);
        }
      }

      switch (status) {
        case 403:
          console.warn('Acceso denegado: No tienes permisos para realizar esta acción');
          break;
        case 404:
          console.warn('Recurso no encontrado');
          break;
        case 500:
          console.error('Error interno del servidor');
          break;
      }
    } else if (error.request) {
      console.error('No se recibió respuesta del servidor');
    } else {
      console.error('Error al configurar la petición:', error.message);
    }
    return Promise.reject(error);
  }
);

export default axiosClient;
